/**
 * Render-safe structural drawing types. Keep this module free of Angular and feature
 * imports so the same geometry can run in the preview thread and export worker.
 */
export const DRAWING_REFERENCE_SIZE = 1080;

export type DrawingPaintToolLike = 'pen' | 'marker' | 'highlighter' | 'eraser';

export interface DrawingPointLike {
  x: number;
  y: number;
  pressure?: number;
  timeMs?: number;
}

export interface DrawingStrokeLike {
  id: string;
  tool: DrawingPaintToolLike;
  color: string;
  weight: number;
  opacity: number;
  points: readonly DrawingPointLike[];
}

export interface DrawingLayerLike {
  strokes: readonly DrawingStrokeLike[];
}

export interface DrawingViewport {
  width: number;
  height: number;
}

export interface DrawingPointRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface DrawingStrokeBounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export function clampDrawingUnit(value: number): number {
  return Math.min(1, Math.max(0, Number.isFinite(value) ? value : 0));
}

export function clampDrawingWeight(value: number): number {
  const finite = Number.isFinite(value) ? value : 1;
  return Math.min(100, Math.max(1, Math.round(finite)));
}

export function clampDrawingOpacity(value: number): number {
  const finite = Number.isFinite(value) ? value : 1;
  return Math.min(1, Math.max(0.01, Math.round(finite * 100) / 100));
}

export function clampDrawingPoint(point: DrawingPointLike): DrawingPointLike {
  const pressure = normalizePressure(point.pressure);
  const timeMs =
    Number.isFinite(point.timeMs) && Number(point.timeMs) >= 0 ? Number(point.timeMs) : undefined;
  return {
    x: clampDrawingUnit(point.x),
    y: clampDrawingUnit(point.y),
    ...(pressure === undefined ? {} : { pressure }),
    ...(timeMs === undefined ? {} : { timeMs }),
  };
}

export function normalizeDrawingPoint(
  clientX: number,
  clientY: number,
  rect: DrawingPointRect,
  pressure?: number,
  timeMs?: number,
): DrawingPointLike {
  const safeWidth = Math.max(1, rect.width);
  const safeHeight = Math.max(1, rect.height);
  return clampDrawingPoint({
    x: (clientX - rect.left) / safeWidth,
    y: (clientY - rect.top) / safeHeight,
    ...(pressure === undefined ? {} : { pressure }),
    ...(timeMs === undefined ? {} : { timeMs }),
  });
}

export function drawingPointDistance(a: DrawingPointLike, b: DrawingPointLike): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/**
 * Adds a point only after the pointer has travelled far enough to matter. Keeping this
 * filtering in normalized space makes gesture density independent of preview resolution.
 */
export function appendDrawingPoint<T extends DrawingPointLike>(
  points: readonly T[],
  point: T,
  minimumDistance = 0.00075,
): readonly T[] {
  const next = clampDrawingPoint(point) as T;
  const last = points.at(-1);
  if (last && drawingPointDistance(last, next) < Math.max(0, minimumDistance)) return points;
  return [...points, next];
}

/** Ramer-Douglas-Peucker simplification keeps stored strokes compact without rasterizing. */
export function simplifyDrawingPoints<T extends DrawingPointLike>(
  points: readonly T[],
  tolerance = 0.0009,
): readonly T[] {
  if (points.length <= 2 || tolerance <= 0)
    return points.map((point) => clampDrawingPoint(point) as T);

  const normalized = points.map((point) => clampDrawingPoint(point) as T);
  const keep = new Uint8Array(normalized.length);
  keep[0] = 1;
  keep[normalized.length - 1] = 1;
  const stack: Array<[number, number]> = [[0, normalized.length - 1]];

  while (stack.length) {
    const [startIndex, endIndex] = stack.pop()!;
    const start = normalized[startIndex];
    const end = normalized[endIndex];
    let furthestIndex = -1;
    let furthestDistance = 0;

    for (let index = startIndex + 1; index < endIndex; index++) {
      const distance = distanceToSegment(normalized[index], start, end, 1, 1);
      if (distance > furthestDistance) {
        furthestDistance = distance;
        furthestIndex = index;
      }
    }

    if (furthestIndex >= 0 && furthestDistance > tolerance) {
      keep[furthestIndex] = 1;
      stack.push([startIndex, furthestIndex], [furthestIndex, endIndex]);
    }
  }

  return normalized.filter((_, index) => keep[index] === 1);
}

/** Evenly downsamples very dense gestures while preserving both endpoints. */
export function limitDrawingPoints<T extends DrawingPointLike>(
  points: readonly T[],
  maximum: number,
): readonly T[] {
  const limit = Math.max(2, Math.floor(maximum));
  if (points.length <= limit) return points;
  const lastIndex = points.length - 1;
  return Array.from({ length: limit }, (_, index) => {
    const sourceIndex = Math.round((index / (limit - 1)) * lastIndex);
    return points[sourceIndex];
  });
}

export function drawingStrokeBounds(stroke: DrawingStrokeLike): DrawingStrokeBounds | null {
  if (!stroke.points.length) return null;
  let left = 1;
  let top = 1;
  let right = 0;
  let bottom = 0;
  for (const point of stroke.points) {
    left = Math.min(left, clampDrawingUnit(point.x));
    top = Math.min(top, clampDrawingUnit(point.y));
    right = Math.max(right, clampDrawingUnit(point.x));
    bottom = Math.max(bottom, clampDrawingUnit(point.y));
  }
  return { left, top, right, bottom };
}

/** Converts a design-space brush weight to the current preview/export pixel size. */
export function drawingWeightInPixels(weight: number, viewport: DrawingViewport): number {
  const shortEdge = Math.max(1, Math.min(viewport.width, viewport.height));
  return Math.max(0.5, (clampDrawingWeight(weight) / DRAWING_REFERENCE_SIZE) * shortEdge);
}

/**
 * Hit-tests visible paint strokes from front to back. Eraser gestures are operations on
 * the isolated layer rather than selectable artwork, so they are intentionally skipped.
 */
export function hitTestDrawingLayer(
  layer: DrawingLayerLike | null,
  point: DrawingPointLike,
  viewport: DrawingViewport,
  tolerancePixels = 8,
): string | null {
  if (!layer || viewport.width <= 0 || viewport.height <= 0) return null;
  const target = clampDrawingPoint(point);
  for (let strokeIndex = layer.strokes.length - 1; strokeIndex >= 0; strokeIndex--) {
    const stroke = layer.strokes[strokeIndex];
    if (stroke.tool === 'eraser' || !stroke.points.length) continue;
    const threshold =
      Math.max(0, tolerancePixels) + drawingWeightInPixels(stroke.weight, viewport) / 2;
    if (stroke.points.length === 1) {
      const only = stroke.points[0];
      const distance = Math.hypot(
        (target.x - only.x) * viewport.width,
        (target.y - only.y) * viewport.height,
      );
      if (distance <= threshold) return stroke.id;
      continue;
    }
    for (let index = 1; index < stroke.points.length; index++) {
      const distance = distanceToSegment(
        target,
        stroke.points[index - 1],
        stroke.points[index],
        viewport.width,
        viewport.height,
      );
      if (distance <= threshold) return stroke.id;
    }
  }
  return null;
}

function normalizePressure(value: number | undefined): number | undefined {
  if (value === undefined || !Number.isFinite(value) || value <= 0) return undefined;
  return Math.min(1, Math.max(0.01, value));
}

function distanceToSegment(
  point: DrawingPointLike,
  start: DrawingPointLike,
  end: DrawingPointLike,
  width: number,
  height: number,
): number {
  const px = point.x * width;
  const py = point.y * height;
  const ax = start.x * width;
  const ay = start.y * height;
  const bx = end.x * width;
  const by = end.y * height;
  const dx = bx - ax;
  const dy = by - ay;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared <= Number.EPSILON) return Math.hypot(px - ax, py - ay);
  const t = Math.min(1, Math.max(0, ((px - ax) * dx + (py - ay) * dy) / lengthSquared));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}
